import {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";

import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useFeatureFlagGate } from "~/shared/hooks/use-feature-flag-gate.ts";
import { useFeatureFlags } from "~/shared/hooks/use-feature-flags.ts";
import {
	useAutoSpinner,
	useGlobalSpinner,
} from "~/shared/hooks/use-global-spinner.ts";
import type { CarrierPrice } from "~/shared/schemas/carrier-schema.ts";
import type {
	Markups,
	MarkupsPartials,
} from "~/shared/schemas/markup-schema.ts";
import type { QuoteResponse } from "~/shared/schemas/quote-schema.ts";
import type {
	CarrierFiltersType,
	FilterKey,
	SortDirection,
} from "~/shared/types/carrier-selection-types.ts";

import { QuoteDetailsContext } from "../../context/quote-details-context.tsx";
import { useCarrierFilters } from "../../hooks/use-carrier-selection-filters-hooks.ts";
import {
	useBookingManager,
	useCarrierPageLoader,
	useGetLtlMarkups,
	useGetPartialsMarkup,
	useMarginManager,
	useModalManager,
	useUpdateAllCarrierMargins,
} from "../../hooks/use-carrier-selection-hooks.ts";
import type { MarginCalculationData } from "../../utils/margin-calculator.ts";
import { useRemoveInsurance } from "./components/add-insurance-modal/add-insurance-hooks.tsx";
import { AddInsuranceModal } from "./components/add-insurance-modal/add-insurance-modal.tsx";
import { CarrierErrorModal } from "./components/carrier-error-modal/carrier-error-modal.tsx";
import { CarrierSelectionPanel } from "./components/carrier-selection-panel/carrier-selection-panel.tsx";
import { RateConfirmationModal } from "./components/rate-confirmation-modal/rate-confirmation-modal.tsx";
import { useRoutingGuideToggle } from "./components/shared/hooks/use-routing-guide-toggle.ts";
import { carrierLiabilityIsInsufficient } from "./utils/carrier-liability.ts";

type CarrierSelectionFormProps = {
	enableShipmentValueModal?: boolean;
	enableTenderOnBook?: boolean;
	isRerate?: boolean;
	onTenderSuccess?: () => void;
	onTenderError?: (message: string) => void;
	onPaymentRequired?: (quote: QuoteResponse) => void;
};

export const CarrierSelectionForm = ({
	enableShipmentValueModal = false,
	enableTenderOnBook = false,
	isRerate = false,
	onTenderSuccess,
	onTenderError,
	onPaymentRequired,
}: CarrierSelectionFormProps = {}) => {
	const { flags } = useFeatureFlags();
	const { isInternal } = useDeploymentType();
	const isSaveQuoteEnabled =
		flags["traxLtlCsSaveButton"] === true && isInternal;
	const isAddInsuranceEnabled = flags["traxLtlCsAddInsurance"] === true;
	const { isEnabled } = useFeatureFlagGate("traxLtlRoutingGuide");

	// Use custom hooks for better separation of concerns
	const { showModal, toggleModal } = useModalManager();
	const {
		marginType,
		marginValue,
		setMarginValue,
		handleMarginTypeChange,
		handleAdjustClick,
		isPending: isAdjustingPrices,
		refreshModal: marginRefreshModal,
	} = useMarginManager();
	const {
		pageData,
		pageDataIsLoading,
		pageDataIsFetched,
		shouldFetchQuote,
		isLoadingQuote,
		quoteIsFetched,
	} = useCarrierPageLoader({ skipQuoteFetch: isRerate });
	const { isBooking, handleStepChange } = useBookingManager();

	const {
		updateFilter,
		setCurrentTab,
		sortDirection,
		setSortDirection,
		getFilteredAndSortedCarriers,
	} = useCarrierFilters();

	const { quoteState, addBlankCarrier, resetForm } =
		useContext(QuoteDetailsContext);

	const {
		updateAllCarrierMargins,
		isPending: isUpdatingMargins,
		refreshModal: updateMarginsRefreshModal,
	} = useUpdateAllCarrierMargins();

	const {
		handleRemoveInsurance: removeInsurance,
		isPending: isRemovingInsurance,
		refreshModal: removeInsuranceRefreshModal,
	} = useRemoveInsurance();

	const filtersDirtyRef = useRef(false);
	const getFilteredAndSortedRef = useRef(getFilteredAndSortedCarriers);
	getFilteredAndSortedRef.current = getFilteredAndSortedCarriers;
	const getCarrierKey = useCallback((carrier: CarrierPrice) => {
		// Include carrierId to ensure uniqueness since id can be 0 for multiple carriers
		// Also include carrier.carrier.id for additional uniqueness when carriers have different nested IDs
		const baseKey = `carrier-${carrier.id}-${carrier.quoteId}-${carrier.carrierId}-${carrier.carrier?.id ?? ""}`;
		// const baseKey = randomUUID();
		return carrier.isAdHoc ? `${baseKey}-adhoc` : baseKey;
	}, []);

	const {
		isGuideActive,
		showingHidden,
		trigger,
		hiddenCarrierCount,
		handleToggleGuide,
		handleToggleHiddenCarriers,
		filteredCarriers: routingGuideCarriers,
	} = useRoutingGuideToggle(quoteState.carrierPrices);

	const [displayedCarriers, setDisplayedCarriers] = useState<CarrierPrice[]>(
		() => {
			return getFilteredAndSortedCarriers(routingGuideCarriers);
		},
	);

	const getNewCarriers = useCallback(
		(
			carrierPrices: CarrierPrice[],
			previousKeys: Set<string>,
		): CarrierPrice[] => {
			return carrierPrices
				.filter((carrier) => !previousKeys.has(getCarrierKey(carrier)))
				.map((carrier) => carrier);
		},
		[getCarrierKey],
	);

	const getUpdatedExistingCarriers = useCallback(
		(
			prevCarriers: CarrierPrice[],
			nextByKey: Map<string, CarrierPrice>,
		): CarrierPrice[] => {
			return prevCarriers
				.map((carrier) => nextByKey.get(getCarrierKey(carrier)))
				.filter((carrier): carrier is CarrierPrice => carrier !== undefined);
		},
		[getCarrierKey],
	);

	const mergeCarriers = useCallback(
		(prevCarriers: CarrierPrice[], nextCarriers: CarrierPrice[]) => {
			const nextByKey = new Map(
				nextCarriers.map((carrier) => [getCarrierKey(carrier), carrier]),
			);
			const previousKeys = new Set(
				prevCarriers.map((carrier) => getCarrierKey(carrier)),
			);

			const newCarriers = getNewCarriers(nextCarriers, previousKeys);
			const updatedExisting = getUpdatedExistingCarriers(
				prevCarriers,
				nextByKey,
			);

			// Only re-sort when new carriers arrive (e.g., initial load, ad-hoc added).
			// When it's just data updates (e.g., margin edits), preserve existing order
			// so cards don't jump around while the user is typing.
			if (newCarriers.length === 0) {
				return updatedExisting;
			}

			return getFilteredAndSortedRef.current([
				...newCarriers,
				...updatedExisting,
			]);
		},
		[getCarrierKey, getNewCarriers, getUpdatedExistingCarriers],
	);

	useEffect(() => {
		setDisplayedCarriers((prevCarriers) =>
			mergeCarriers(prevCarriers, routingGuideCarriers),
		);
	}, [routingGuideCarriers, mergeCarriers]);

	useEffect(() => {
		if (filtersDirtyRef.current) {
			setDisplayedCarriers(
				getFilteredAndSortedRef.current(routingGuideCarriers),
			);
			filtersDirtyRef.current = false;
		}
	}, [getFilteredAndSortedCarriers, routingGuideCarriers]);

	const handleFilterChange = useCallback(
		<K extends FilterKey>(key: K, value: CarrierFiltersType[K]) => {
			filtersDirtyRef.current = true;
			updateFilter(key, value);
		},
		[updateFilter],
	);

	const handleTabSelect = useCallback(
		(tab: number) => {
			filtersDirtyRef.current = true;
			setCurrentTab(tab);
		},
		[setCurrentTab],
	);

	const handleSortDirectionChange = useCallback(
		(direction: SortDirection) => {
			filtersDirtyRef.current = true;
			setSortDirection(direction);
		},
		[setSortDirection],
	);

	const isLoading =
		!pageData ||
		(pageDataIsLoading && !pageDataIsFetched) ||
		(shouldFetchQuote && isLoadingQuote && !quoteIsFetched) ||
		isBooking ||
		isRemovingInsurance ||
		isUpdatingMargins;

	// Automatically manage global spinner for page data loading
	useAutoSpinner(isLoading);

	// Get showSpinner for carrier booking callbacks
	const { showSpinner, hideSpinner } = useGlobalSpinner();

	const shouldShowCarrierError = useMemo(() => {
		// Only show error if all data is loaded AND we have attempted to load carrier prices
		// Check if we have a valid quote state with an ID (meaning quote was loaded)
		const hasValidQuote = quoteState.id > 0;
		const quoteIsReady = quoteIsFetched || !shouldFetchQuote;
		const carrierPricesAttempted = hasValidQuote && quoteIsReady;

		// Additional check: ensure we're not in initial loading states
		const isInitialLoading =
			(shouldFetchQuote && !quoteIsFetched) ||
			pageDataIsLoading ||
			isLoadingQuote;

		// Be conservative - don't show until we have definitive data
		return (
			!isLoading &&
			!isInitialLoading &&
			pageDataIsFetched &&
			carrierPricesAttempted &&
			hasValidQuote &&
			quoteState.carrierPrices.length === 0 &&
			// Additional safety: only if we have pageData
			Boolean(pageData)
		);
	}, [
		isLoading,
		shouldFetchQuote,
		pageDataIsLoading,
		isLoadingQuote,
		quoteIsFetched,
		pageDataIsFetched,
		quoteState.id,
		quoteState.carrierPrices.length,
		pageData,
	]);

	const [isCarrierErrorDismissed, setIsCarrierErrorDismissed] =
		useState<boolean>(false);

	useEffect(() => {
		if (!shouldShowCarrierError || quoteState.carrierPrices.length > 0) {
			setIsCarrierErrorDismissed(false);
		}
	}, [shouldShowCarrierError, quoteState.carrierPrices.length, quoteState.id]);

	const shouldShowCarrierErrorModal = Boolean(
		shouldShowCarrierError && !isCarrierErrorDismissed,
	);

	//Get Markup Defaults
	const [marginCalculationData, setMarginCalculationData] =
		useState<MarginCalculationData>({
			ltlMarkups: null,
			customerLtlMarkups: null,
			partialsMarkups: null,
			customerPartialsMarkups: null,
		});
	const { mutateAsync: getLTLMarkup } = useGetLtlMarkups();
	const { mutateAsync: getPartialsMarkup } = useGetPartialsMarkup();
	const hasFetchedRef = useRef(false);

	const updateMarginData = useCallback(
		(key: keyof MarginCalculationData, data: Markups | MarkupsPartials) => {
			setMarginCalculationData((prev) => ({ ...prev, [key]: data }));
		},
		[],
	);

	const fetchAllMarkups = useCallback(
		async (customerId: number) => {
			const results = await Promise.all([
				getLTLMarkup({ customerId: undefined }),
				getLTLMarkup({ customerId }),
				getPartialsMarkup({ customerId: undefined }),
				getPartialsMarkup({ customerId }),
			]);

			const [
				ltlMarkup,
				customerLtlMarkup,
				partialsMarkup,
				customerPartialsMarkup,
			] = results;

			if (ltlMarkup.success)
				updateMarginData("ltlMarkups", ltlMarkup.data as Markups);
			if (customerLtlMarkup.success)
				updateMarginData(
					"customerLtlMarkups",
					customerLtlMarkup.data as Markups,
				);
			if (partialsMarkup.success)
				updateMarginData(
					"partialsMarkups",
					partialsMarkup.data as MarkupsPartials,
				);
			if (customerPartialsMarkup.success)
				updateMarginData(
					"customerPartialsMarkups",
					customerPartialsMarkup.data as MarkupsPartials,
				);
		},
		[getLTLMarkup, getPartialsMarkup, updateMarginData],
	);

	useEffect(() => {
		if (!quoteState.customer.id || quoteState.customer.id <= 0 || !isInternal)
			return;
		if (hasFetchedRef.current) return;
		hasFetchedRef.current = true;
		fetchAllMarkups(quoteState.customer.id);
	}, [fetchAllMarkups, quoteState.customer.id, isInternal]);

	const handleRemoveInsuranceClick = async () => {
		let eTagForRemoval = quoteState.eTag;

		if (isInternal) {
			//Save carrier margins first (internal only - external users cannot edit margins)
			const updatedETag = await updateAllCarrierMargins(
				quoteState.id,
				quoteState.eTag,
				quoteState.carrierPrices,
			);
			if (!updatedETag) return;
			eTagForRemoval = updatedETag;
		}

		await removeInsurance({
			eTag: eTagForRemoval,
			id: quoteState.id,
		});
	};

	// Show a page-level red banner when any displayed carrier's liability does
	// not cover the shipment value.
	const showLiabilityBanner = displayedCarriers.some((carrier) =>
		carrierLiabilityIsInsufficient(
			carrier,
			quoteState.insuranceValue,
			quoteState.insuranceApplyTypeId,
			quoteState.insuranceProductTypeId,
		),
	);
	const showRoutingGuideBanner = isEnabled && isInternal;

	return (
		<>
			{marginRefreshModal}
			{updateMarginsRefreshModal}
			{removeInsuranceRefreshModal}
			<AddInsuranceModal
				defaultProductType={quoteState.insuranceProductTypeId ?? undefined}
				defaultShipmentValue={quoteState.insuranceValue}
				onToggleModal={toggleModal}
				quoteId={quoteState.id}
				showModal={showModal.addInsurance}
			/>
			<RateConfirmationModal
				carriers={displayedCarriers}
				onToggleModal={toggleModal}
				showModal={showModal.confirmationRate}
			/>
			<CarrierErrorModal
				onDismiss={() => setIsCarrierErrorDismissed(true)}
				resetForm={resetForm}
				showModal={shouldShowCarrierErrorModal}
			/>
			<CarrierSelectionPanel
				addBlankCarrier={addBlankCarrier}
				carrierPrices={quoteState.carrierPrices}
				displayedCarriers={displayedCarriers}
				enableShipmentValueModal={enableShipmentValueModal}
				enableTenderOnBook={enableTenderOnBook}
				hasRoutingGuides={quoteState.hasRoutingGuideMatch || false}
				hiddenCarrierCount={hiddenCarrierCount}
				isAddInsuranceEnabled={isAddInsuranceEnabled}
				isAdjustingPrices={isAdjustingPrices}
				isBookingDisabled={(carrier) =>
					Boolean(
						isGuideActive &&
						carrier.routingGuideApplied !== true &&
						!carrier.isAdHoc,
					)
				}
				isGuideActive={isGuideActive}
				isRemovingInsurance={isRemovingInsurance}
				isRerate={isRerate}
				isSaveQuoteEnabled={isSaveQuoteEnabled}
				isUpdatingMargins={isUpdatingMargins}
				marginCalculationData={marginCalculationData}
				marginType={marginType}
				marginValue={marginValue ?? 0}
				onAdjustClick={handleAdjustClick}
				onBookingEnd={hideSpinner}
				onBookingStart={showSpinner}
				onFilterChange={handleFilterChange}
				onMarginTypeChange={handleMarginTypeChange}
				onPaymentRequired={onPaymentRequired}
				onRemoveInsurance={() => {
					void handleRemoveInsuranceClick();
				}}
				onSetMarginValue={setMarginValue}
				onSortDirectionChange={handleSortDirectionChange}
				onStepChange={handleStepChange}
				onTabSelect={handleTabSelect}
				onTenderError={onTenderError}
				onTenderSuccess={onTenderSuccess}
				onToggleGuide={handleToggleGuide}
				onToggleHiddenCarriers={handleToggleHiddenCarriers}
				onToggleModal={toggleModal}
				onUpdateAllCarrierMargins={(quoteId, eTag, carriers) => {
					void updateAllCarrierMargins(quoteId, eTag, carriers);
				}}
				pageData={pageData}
				showLiabilityBanner={showLiabilityBanner}
				showRoutingGuideBanner={showRoutingGuideBanner}
				showingHidden={showingHidden}
				sortDirection={sortDirection}
				trigger={trigger}
			/>
		</>
	);
};
